import React from "react";
import { api, apiErr, API } from "../lib/api";
import { mergeContent } from "../lib/siteContent";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Save, Plus, Trash2, ExternalLink, ImageIcon, Loader2, GripVertical,
} from "lucide-react";

const TABS = [
  { value: "hero", label: "Hero" },
  { value: "produce", label: "What We Produce" },
  { value: "why", label: "Why Choose Us" },
  { value: "about", label: "About" },
  { value: "testimonials", label: "Testimonials" },
  { value: "updates", label: "Updates" },
  { value: "contact", label: "Contact" },
  { value: "footer", label: "Footer" },
];

/* ---------- Small field helpers ---------- */
function Field({ label, value, onChange, textarea, placeholder, testid }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-muted-foreground">{label}</Label>
      {textarea ? (
        <Textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={3}
          data-testid={testid}
        />
      ) : (
        <Input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          data-testid={testid}
        />
      )}
    </div>
  );
}

function ImageField({ label, fileId, onChange, testid }) {
  const [busy, setBusy] = React.useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/content/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.file_id);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-muted-foreground">{label}</Label>
      <div className="flex items-center gap-3">
        <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border bg-muted">
          {fileId ? (
            <img src={`${API}/public/files/${fileId}`} alt="" className="h-full w-full object-cover" />
          ) : (
            <ImageIcon className="h-5 w-5 text-muted-foreground" />
          )}
        </div>
        <div className="flex flex-col gap-1.5">
          <Input
            type="file"
            accept="image/*"
            className="text-xs"
            disabled={busy}
            onChange={(e) => upload(e.target.files[0])}
            data-testid={testid}
          />
          {fileId && (
            <button
              type="button"
              onClick={() => onChange("")}
              className="self-start text-xs text-destructive hover:underline"
              data-testid={`${testid}-remove`}
            >
              Remove image
            </button>
          )}
          {busy && (
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" /> Uploading…
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function ListCard({ children, onRemove, testid }) {
  return (
    <Card className="relative space-y-3 p-4">
      <div className="flex items-center justify-between">
        <GripVertical className="h-4 w-4 text-muted-foreground/50" />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-destructive hover:text-destructive"
          onClick={onRemove}
          data-testid={testid}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
      {children}
    </Card>
  );
}

/* ---------- Main editor ---------- */
export function WebsiteContent() {
  const [content, setContent] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/content");
      setContent(mergeContent(data));
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/content", content);
      toast.success("Website content saved. Changes are live.");
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSaving(false);
    }
  };

  // setters
  const setSection = (section, key, value) =>
    setContent((c) => ({ ...c, [section]: { ...c[section], [key]: value } }));
  const setList = (section, idx, key, value) =>
    setContent((c) => ({
      ...c,
      [section]: c[section].map((it, i) => (i === idx ? { ...it, [key]: value } : it)),
    }));
  const addItem = (section, blank) =>
    setContent((c) => ({ ...c, [section]: [...c[section], blank] }));
  const removeItem = (section, idx) =>
    setContent((c) => ({ ...c, [section]: c[section].filter((_, i) => i !== idx) }));
  // string lists (updates, about.values/gallery)
  const setStrList = (section, idx, value) =>
    setContent((c) => ({ ...c, [section]: c[section].map((s, i) => (i === idx ? value : s)) }));
  const setNestedStrList = (section, key, idx, value) =>
    setContent((c) => ({
      ...c,
      [section]: { ...c[section], [key]: c[section][key].map((s, i) => (i === idx ? value : s)) },
    }));
  const addNestedStr = (section, key) =>
    setContent((c) => ({ ...c, [section]: { ...c[section], [key]: [...c[section][key], ""] } }));
  const removeNestedStr = (section, key, idx) =>
    setContent((c) => ({
      ...c,
      [section]: { ...c[section], [key]: c[section][key].filter((_, i) => i !== idx) },
    }));

  if (loading || !content) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center" data-testid="content-loading">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6" data-testid="website-content-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight">Website Content</h1>
          <p className="text-sm text-muted-foreground">
            Edit the public home page. Changes go live immediately after saving.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <a href="/" target="_blank" rel="noreferrer">
            <Button variant="outline" size="sm" data-testid="view-site-btn">
              <ExternalLink className="mr-1.5 h-4 w-4" /> View site
            </Button>
          </a>
          <Button onClick={save} disabled={saving} data-testid="save-content-btn">
            {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />}
            Save changes
          </Button>
        </div>
      </div>

      <Tabs defaultValue="hero" className="w-full">
        <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 bg-muted/60 p-1">
          {TABS.map((t) => (
            <TabsTrigger key={t.value} value={t.value} data-testid={`tab-${t.value}`} className="text-xs">
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* HERO */}
        <TabsContent value="hero" className="mt-4">
          <Card className="space-y-4 p-5">
            <Field label="Location badge" value={content.hero.badge} onChange={(v) => setSection("hero", "badge", v)} testid="hero-badge" />
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Title line 1" value={content.hero.title1} onChange={(v) => setSection("hero", "title1", v)} testid="hero-title1" />
              <Field label="Title line 2 (accent)" value={content.hero.title2} onChange={(v) => setSection("hero", "title2", v)} testid="hero-title2" />
            </div>
            <Field label="Subtitle" value={content.hero.subtitle} onChange={(v) => setSection("hero", "subtitle", v)} textarea testid="hero-subtitle" />
            <ImageField label="Hero background image" fileId={content.hero.image_file} onChange={(v) => setSection("hero", "image_file", v)} testid="hero-image" />
          </Card>
        </TabsContent>

        {/* PRODUCE */}
        <TabsContent value="produce" className="mt-4 space-y-3">
          {content.produce.map((p, i) => (
            <ListCard key={i} onRemove={() => removeItem("produce", i)} testid={`produce-remove-${i}`}>
              <Field label="Title" value={p.title} onChange={(v) => setList("produce", i, "title", v)} testid={`produce-title-${i}`} />
              <Field label="Description" value={p.text} onChange={(v) => setList("produce", i, "text", v)} textarea testid={`produce-text-${i}`} />
            </ListCard>
          ))}
          <Button variant="outline" onClick={() => addItem("produce", { title: "", text: "" })} data-testid="produce-add">
            <Plus className="mr-1.5 h-4 w-4" /> Add produce item
          </Button>
        </TabsContent>

        {/* WHY */}
        <TabsContent value="why" className="mt-4 space-y-3">
          {content.why.map((w, i) => (
            <ListCard key={i} onRemove={() => removeItem("why", i)} testid={`why-remove-${i}`}>
              <Field label="Title" value={w.title} onChange={(v) => setList("why", i, "title", v)} testid={`why-title-${i}`} />
              <Field label="Description" value={w.text} onChange={(v) => setList("why", i, "text", v)} textarea testid={`why-text-${i}`} />
            </ListCard>
          ))}
          <Button variant="outline" onClick={() => addItem("why", { title: "", text: "" })} data-testid="why-add">
            <Plus className="mr-1.5 h-4 w-4" /> Add reason
          </Button>
        </TabsContent>

        {/* ABOUT */}
        <TabsContent value="about" className="mt-4">
          <Card className="space-y-4 p-5">
            <Field label="Section title" value={content.about.title} onChange={(v) => setSection("about", "title", v)} testid="about-title" />
            <Field label="Story paragraph 1" value={content.about.story1} onChange={(v) => setSection("about", "story1", v)} textarea testid="about-story1" />
            <Field label="Story paragraph 2" value={content.about.story2} onChange={(v) => setSection("about", "story2", v)} textarea testid="about-story2" />
            <ImageField label="About image" fileId={content.about.image_file} onChange={(v) => setSection("about", "image_file", v)} testid="about-image" />

            <div className="space-y-2">
              <Label className="text-xs font-medium text-muted-foreground">Core values</Label>
              {content.about.values.map((val, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input value={val} onChange={(e) => setNestedStrList("about", "values", i, e.target.value)} data-testid={`about-value-${i}`} />
                  <Button variant="ghost" size="icon" className="text-destructive" onClick={() => removeNestedStr("about", "values", i)} data-testid={`about-value-remove-${i}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => addNestedStr("about", "values")} data-testid="about-value-add">
                <Plus className="mr-1.5 h-4 w-4" /> Add value
              </Button>
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-medium text-muted-foreground">Gallery captions</Label>
              {content.about.gallery.map((val, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input value={val} onChange={(e) => setNestedStrList("about", "gallery", i, e.target.value)} data-testid={`about-gallery-${i}`} />
                  <Button variant="ghost" size="icon" className="text-destructive" onClick={() => removeNestedStr("about", "gallery", i)} data-testid={`about-gallery-remove-${i}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => addNestedStr("about", "gallery")} data-testid="about-gallery-add">
                <Plus className="mr-1.5 h-4 w-4" /> Add caption
              </Button>
            </div>
          </Card>
        </TabsContent>

        {/* TESTIMONIALS */}
        <TabsContent value="testimonials" className="mt-4 space-y-3">
          {content.testimonials.map((t, i) => (
            <ListCard key={i} onRemove={() => removeItem("testimonials", i)} testid={`testimonial-remove-${i}`}>
              <Field label="Quote" value={t.quote} onChange={(v) => setList("testimonials", i, "quote", v)} textarea testid={`testimonial-quote-${i}`} />
              <Field label="Attribution" value={t.by} onChange={(v) => setList("testimonials", i, "by", v)} testid={`testimonial-by-${i}`} />
              <ImageField label="Photo (optional)" fileId={t.image_file} onChange={(v) => setList("testimonials", i, "image_file", v)} testid={`testimonial-image-${i}`} />
            </ListCard>
          ))}
          <Button variant="outline" onClick={() => addItem("testimonials", { quote: "", by: "", image_file: "" })} data-testid="testimonial-add">
            <Plus className="mr-1.5 h-4 w-4" /> Add testimonial
          </Button>
        </TabsContent>

        {/* UPDATES */}
        <TabsContent value="updates" className="mt-4 space-y-3">
          {content.updates.map((u, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input value={u} onChange={(e) => setStrList("updates", i, e.target.value)} placeholder="Update headline" data-testid={`update-${i}`} />
              <Button variant="ghost" size="icon" className="text-destructive" onClick={() => removeItem("updates", i)} data-testid={`update-remove-${i}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <Button variant="outline" onClick={() => addItem("updates", "")} data-testid="update-add">
            <Plus className="mr-1.5 h-4 w-4" /> Add update
          </Button>
        </TabsContent>

        {/* CONTACT */}
        <TabsContent value="contact" className="mt-4">
          <Card className="space-y-4 p-5">
            <Field label="Address" value={content.contact.address} onChange={(v) => setSection("contact", "address", v)} textarea testid="contact-address" />
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Phone" value={content.contact.phone} onChange={(v) => setSection("contact", "phone", v)} testid="contact-phone" />
              <Field label="Email" value={content.contact.email} onChange={(v) => setSection("contact", "email", v)} testid="contact-email" />
              <Field label="WhatsApp number (digits only)" value={content.contact.whatsapp} onChange={(v) => setSection("contact", "whatsapp", v)} testid="contact-whatsapp" />
              <Field label="Instagram URL" value={content.contact.instagram} onChange={(v) => setSection("contact", "instagram", v)} testid="contact-instagram" />
              <Field label="Facebook URL" value={content.contact.facebook} onChange={(v) => setSection("contact", "facebook", v)} testid="contact-facebook" />
              <Field label="TikTok URL" value={content.contact.tiktok} onChange={(v) => setSection("contact", "tiktok", v)} testid="contact-tiktok" />
            </div>
          </Card>
        </TabsContent>

        {/* FOOTER */}
        <TabsContent value="footer" className="mt-4">
          <Card className="space-y-4 p-5">
            <Field label="Tagline" value={content.footer.tagline} onChange={(v) => setSection("footer", "tagline", v)} testid="footer-tagline" />
            <Field label="Subline" value={content.footer.subline} onChange={(v) => setSection("footer", "subline", v)} testid="footer-subline" />
          </Card>
        </TabsContent>
      </Tabs>

      <div className="flex justify-end border-t border-border pt-4">
        <Button onClick={save} disabled={saving} data-testid="save-content-btn-bottom">
          {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />}
          Save changes
        </Button>
      </div>
    </div>
  );
}

export default WebsiteContent;
